<script setup lang="ts">
import { ref } from 'vue';
import axios from 'axios';
// 💡 注意：如果你的 Element Plus 图标是按需引入的，需要在这里导入
import { View, Star } from '@element-plus/icons-vue'; 

// ================= 1. 定义 Props 类型 =================
// 根据模板推断出 obj 的数据结构
interface ItemData {
  guid: string;
  image: string;
  link: string;
  icon: string;
  title: string;
  desc: string;
  hit: number;
  rate: number;
}

const props = defineProps<{
  obj: ItemData;
}>();

// ================= 2. 获取全局变量 =================

// 直接使用环境变量，避免 getCurrentInstance 在生产环境的问题
const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8003';

// ================= 3. 响应式状态 (替代 data) =================
const isHited = ref(false);
const isRated = ref(false);
const hitres = ref<any>(null);
const rateres = ref<any>(null);

// ================= 4. 处理动态 Ref (核心难点) =================
// 在 Vue3 中，处理动态 ref 的最佳实践是使用一个对象来存储，并绑定一个函数
const imgRefs = ref<Record<string, HTMLImageElement | null>>({});

// 收集 DOM 的回调函数
const setImgRef = (el: any) => {
  if (el && props.obj.guid) {
    imgRefs.value[props.obj.guid] = el as HTMLImageElement;
  }
};

// ================= 5. 方法 (替代 methods) =================

const handleShow = (e: ItemData) => {
  console.log('@/assets/' + e.image);
  
  // 从我们收集的对象中获取对应的 DOM 元素
  const imgEl = imgRefs.value[e.guid];
  if (imgEl) {
    // 💡 注意：原代码中写了 this.$refs[e.guid][0]，在 Vue3 函数 ref 中，
    // 获取到的直接就是 DOM 元素本身，不需要 [0] 索引。
    imgEl.src = "images/" + e.image;
  }
};

const hit = (guid: string, url: string) => {
  window.open(url, "_blank");

  if (!isHited.value) {
    // 💡 在 Vue3 中，修改 prop 对象的内部属性（如 obj.hit）是允许的，不会触发警告
    props.obj.hit += 1;
    isHited.value = true;
    
    // http get 通知 php，修改数据库
    axios
      .get(apiBaseUrl + '/api/update_hit_rate/hit/' + guid)
            .then(response => (hitres.value = response))
            .catch(function (error) {
                console.log(error);

      });

    // 10% 概率触发同步 JSON
    if (Math.random() < 1) {
        syncHitRateJson();
    }
  }
};

const rate = (guid: string) => {
  if (!isRated.value) {
    props.obj.rate = props.obj.rate + 1;
        isRated.value = true;
        axios
            .get(apiBaseUrl + '/api/update_hit_rate/rate/' + guid)
            .then(response => (rateres.value = response))
            .catch(function (error) {
                console.log(error);
            });

        // 10% 概率触发同步 JSON
        if (Math.random() < 1) {
            syncHitRateJson();
        }
  }
};

// 同步 hit 和 rate 到 JSON 文件
const syncHitRateJson = () => {
    axios
        .get(apiBaseUrl + '/api/update_hit_rate_json')
        .then(response => {
            console.log('JSON synced:', response.data);
        })
        .catch(function (error) {
            console.log('Sync JSON failed:', error);
        });
};

</script>

<template>
  <el-col :span="6">
    <!-- @show 事件触发时传入 obj -->
    <el-popover placement="top-start" :width="625" trigger="hover" @show="handleShow(obj)">
      
      <!-- 💡 核心修改：将 :ref="obj.guid" 改为 :ref="setImgRef" -->
      <img :ref="setImgRef" :width="600" @click="hit(obj.guid, obj.link)" />
      
      <template #reference>
        <el-card class="card" shadow="hover" :body-style="{ padding: '0' }">
          <div class="icon-title" @click="hit(obj.guid, obj.link)">
            <img :src="'icons/' + obj.icon" alt="图" />
            <span class="title-span">{{ obj.title }}</span>
          </div>

          <div class="desc" @click="hit(obj.guid, obj.link)">{{ obj.desc }}</div>

          <div class="status">
            <span class="hit-span" @click="hit(obj.guid, obj.link)">
              <el-icon><View /></el-icon>
              {{ obj.hit }}
            </span>
            <span class="ratehit-span" @click="rate(obj.guid)">
              <el-icon><Star /></el-icon>
              {{ obj.rate }}
            </span>
          </div>
        </el-card>
      </template>
    </el-popover>
  </el-col>
</template>

<style lang="scss">
.card {
  margin-top: 10px;
  margin-bottom: 10px;

  .icon-title {
    display: block;
    width: 100%;
    padding: 10px 8% 5px 8%;
    box-sizing: border-box;
    transition: none;
    border-radius: 10px;
    text-decoration: none;
    cursor: pointer;

    img {
      float: left;
      width: 26px;
      height: 26px;
    }

    .title-span {
      color: #3c3c3c;
      font-size: 14px;
      line-height: 26px;
      overflow: hidden;
      margin-left: 20px;
    }
  }

  .desc {
    color: #8f8f8f;
    font-size: 12px;
    line-height: 1.5em;
    overflow: hidden;
    padding: 10px 8% 5px 8%;
    cursor: pointer;
  }

  .status {
    display: block;
    width: 100%;
    color: #8f8f8f;
    padding: 10px 8% 5px 8%;
    min-width: 100px;
    max-width: 600px;

    .hit-span {
      font-size: 1em;
      cursor: pointer;
    }

    .ratehit-span {
      padding-right: 30px;
      font-size: 1em;
      width: 50px;
      float: right;
    }
  }

  div span:hover {
    color: crimson;
  }
}
</style>